import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useSelector } from "react-redux";

// layout constants
import { LayoutTypes } from "../constants/layout";

// strore
import { RootState } from "../redux/store";

// All layouts containers
import DefaultLayout from "../layouts/Default";
import VerticalLayout from "../layouts/Vertical";
import DetachedLayout from "../layouts/Detached";
import HorizontalLayout from "../layouts/Horizontal/";
import TwoColumnLayout from "../layouts/TwoColumn/";

import {
  authProtectedFlattenRoutes,
  publicProtectedFlattenRoutes,
} from "./index";

import { APICore } from "../helpers/api/apiCore";
import { isRouteAccessible, getDefaultAccessibleRoute } from "../helpers/menu";

interface RoutesProps {}

const AllRoutes = (props: RoutesProps) => {
  const { layout, user } = useSelector((state: RootState) => ({
    layout: state.Layout,
    user: state.Auth.user,
  }));

  const getLayout = () => {
    let layoutCls = TwoColumnLayout;

    switch (layout.layoutType) {
      case LayoutTypes.LAYOUT_HORIZONTAL:
        layoutCls = HorizontalLayout;
        break;
      case LayoutTypes.LAYOUT_DETACHED:
        layoutCls = DetachedLayout;
        break;
      case LayoutTypes.LAYOUT_VERTICAL:
        layoutCls = VerticalLayout;
        break;
      default:
        layoutCls = TwoColumnLayout;
        break;
    }
    return layoutCls;
  };

  let Layout = getLayout();
  const api = new APICore();

  return (
    <React.Fragment>
      <Routes>
        {/* "/" kept hidden - straight to the login page instead of the public
            landing page. Old behavior kept commented, not deleted, for an
            easy revert. */}
        {/* <Route path="/" element={<Navigate to="/home" replace />} /> */}
        <Route path="/" element={<Navigate to="/auth/login" replace />} />

        <Route>
          {publicProtectedFlattenRoutes.map((route, idx) => (
            <Route
              path={route.path} // /auth/login, /auth/register
              element={
                <DefaultLayout {...props} layout={layout}>
                  {route.element}
                </DefaultLayout>
              }
              key={idx}
            />
          ))}
        </Route>

        <Route>
          {authProtectedFlattenRoutes.map((route, idx) => {
            // menuAccess only gates admin.biharsportsmashaal.in's own
            // Superior-Admin-family login (hobbies === "Cricket", same check
            // LeftSidebar.tsx uses to decide the sidebar itself) - other
            // login types passing through this same route table are left
            // unrestricted, as before.
            const isAdminPanelUser = user?.hobbies === "Cricket";
            const accessible =
              !isAdminPanelUser ||
              isRouteAccessible(route.path as string, user?.menuAccess);
            const fallbackRoute = isAdminPanelUser
              ? getDefaultAccessibleRoute(user?.menuAccess)
              : null;

            return (
              <Route
                path={route.path}
                element={
                  api.isUserAuthenticated() === false ? (
                    <Navigate
                      to={{
                        pathname: "auth/login",
                        search: "next=" + route.path,
                      }}
                    />
                  ) : !accessible && fallbackRoute ? (
                    // Requested a page menuAccess doesn't grant (typed the
                    // URL directly, stale bookmark, etc.) - land them on a
                    // page they do have instead of a blank/broken screen.
                    // No redirect at all if they have zero granted pages
                    // (fallbackRoute null) - that's a misconfigured user, and
                    // redirecting would only bounce them between here and
                    // the login page's own default-route redirect.
                    <Navigate to={fallbackRoute} replace />
                  ) : (
                    <Layout {...props}>{route.element}</Layout>
                  )
                }
                key={idx}
              />
            );
          })}
        </Route>
        {/* Add the catch-all route to redirect to the home page */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </React.Fragment>
  );
};

export default AllRoutes;
